import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, HardHat, PencilRuler, Hammer, Phone, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { api, errMsg } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { LocateMe } from "@/components/LocateMe";
import { nm } from "@/pages/BookCleaning";
import { DesignGallery } from "@/components/DesignGallery";

const F = ({ label, children, wide }) => <div className={`space-y-1.5 ${wide ? "sm:col-span-2" : ""}`}><Label className="text-sm text-slate-700">{label}</Label>{children}</div>;
const sel = "h-10 w-full rounded-md border border-input bg-white px-3 text-sm";
const BUDGETS = ["< $50k", "$50k – $100k", "$100k – $250k", "$250k – $500k", "$500k +"];
const TIMELINES = ["asap", "3", "6", "12", "explore"];

function BuilderCard({ b, picked, onPick, t, lang }) {
  const initials = b.name.split(" ").map((w) => w[0]).filter((c) => /[A-Z]/.test(c)).slice(0, 2).join("");
  return (
    <div className={`rounded-3xl border-2 bg-white p-6 transition-colors ${picked ? "border-blue-600 shadow-lg" : "border-slate-200"}`} data-testid={`builder-card-${b.key}`}>
      <div className="flex items-center gap-4">
        <span className="h-16 w-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white text-xl font-bold flex items-center justify-center">{initials}</span>
        <div><p className="text-lg font-bold text-slate-900" data-testid={`builder-name-${b.key}`}>{b.name}</p><p className="text-sm text-slate-500">{b.company}</p></div>
      </div>
      <p className="mt-4 text-sm font-semibold text-blue-700">{nm(b, lang, "specialty")}</p>
      <p className="mt-1 text-sm text-slate-600">{nm(b, lang, "bio")}</p>
      {b.phone && <a href={`tel:${b.phone}`} className="mt-3 inline-flex items-center gap-1 text-sm text-slate-700"><Phone className="h-4 w-4" />{b.phone}</a>}
      <Button type="button" onClick={onPick} className={`mt-5 w-full rounded-full ${picked ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-900 hover:bg-slate-800"}`} data-testid={`builder-pick-${b.key}`}>
        {picked ? <><CheckCircle2 className="h-4 w-4 me-2" />{t("b.picked")}</> : t("b.pick")}
      </Button>
    </div>
  );
}

export default function BuildHome() {
  const { t, lang } = useI18n();
  useSeo(t("b.title"), t("b.sub"));
  const [builders, setBuilders] = useState([]);
  const [f, setF] = useState({ name: "", phone: "", email: "", city: "", land: "", lot_size: "", home_size: "", bedrooms: "", bathrooms: "", budget: "", timeline: "", style: "", description: "", builder: "any", contact_method: "whatsapp", geo: null });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => { api.get("/builders").then((r) => setBuilders(r.data)); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pick = (k) => { setF({ ...f, builder: k }); document.getElementById("build-form")?.scrollIntoView({ behavior: "smooth" }); };
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.phone.trim() || !f.city.trim()) { toast.error(t("req.required")); return; }
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, k === "geo" ? (v ? JSON.stringify(v) : "") : v));
    files.forEach((x) => fd.append("files", x));
    try { const r = await api.post("/build-leads", fd); setDone(r.data.ref); window.scrollTo(0, 0); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  if (done) return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="build-success">
      <CheckCircle2 className="h-14 w-14 text-blue-600 mx-auto" /><p className="mt-4 text-xl font-semibold">{t("b.done", { ref: done })}</p>
    </div>
  );
  return (
    <div data-testid="build-page">
      <section className="relative overflow-hidden">
        <img src="/img/build-hero.jpg" alt="Custom tropical home with palm trees in Nicaragua" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/60 to-slate-950/10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 text-white">
          <h1 className="max-w-2xl text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">{t("b.title")}</h1>
          <p className="mt-5 max-w-xl text-slate-200">{t("b.sub")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/build/design" className="inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold px-7 h-12 transition-colors" data-testid="build-design-btn"><Sparkles className="h-4 w-4" />{t("dz.cta")}</Link>
            <a href="#build-form" className="inline-flex items-center rounded-full border border-white/60 hover:bg-white/10 text-white font-semibold px-7 h-12 transition-colors" data-testid="build-cta-btn">{t("b.cta")}</a>
          </div>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-3 gap-5">
        {[[HardHat, "b.w1"], [PencilRuler, "b.w2"], [Hammer, "b.w3"]].map(([Icon, k]) => (
          <div key={k} className="rounded-3xl bg-white border border-slate-200 p-6">
            <span className="h-12 w-12 rounded-2xl bg-gradient-to-br from-sky-400 to-blue-600 text-white flex items-center justify-center"><Icon className="h-6 w-6" /></span>
            <p className="mt-4 font-semibold text-lg">{t(k)}</p><p className="text-sm text-slate-600 mt-1">{t(`${k}_d`)}</p>
          </div>
        ))}
      </section>
      <DesignGallery />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("b.rec")}</h2>
        <div className="mt-6 grid md:grid-cols-2 gap-5">
          {builders.map((b) => <BuilderCard key={b.key} b={b} picked={f.builder === b.key} onPick={() => pick(b.key)} t={t} lang={lang} />)}
        </div>
      </section>
      <section id="build-form" className="max-w-4xl mx-auto px-4 pb-28 scroll-mt-24">
        <form onSubmit={submit} className="rounded-3xl bg-white border border-slate-200 p-6 md:p-8 grid sm:grid-cols-2 gap-4" data-testid="build-form">
          <h2 className="sm:col-span-2 text-2xl font-bold tracking-tight">{t("b.form")}</h2>
          <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" value={f.website || ""} onChange={set("website")} />
          <F label={t("b.builder")} wide>
            <div className="flex flex-wrap gap-2">
              {[...builders.map((b) => [b.key, b.name]), ["any", t("b.any")]].map(([k, l]) => (
                <button type="button" key={k} onClick={() => setF({ ...f, builder: k })} data-testid={`build-builder-${k}`}
                  className={`px-4 py-2 rounded-full border text-sm transition-colors ${f.builder === k ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-300 hover:border-blue-400"}`}>{l}</button>
              ))}
            </div>
          </F>
          <F label={`${t("f.name")} *`}><Input value={f.name} onChange={set("name")} data-testid="build-name-input" /></F>
          <F label={`${t("f.phone")} *`}><Input value={f.phone} onChange={set("phone")} data-testid="build-phone-input" /></F>
          <F label={t("f.email")}><Input type="email" value={f.email} onChange={set("email")} data-testid="build-email-input" /></F>
          <F label={`${t("f.city")} *`}><Input value={f.city} onChange={set("city")} data-testid="build-city-input" /></F>
          <F label={t("b.land")}>
            <select className={sel} value={f.land} onChange={set("land")} data-testid="build-land-select">
              <option value="">{t("b.choose")}</option>{["have", "looking", "no"].map((k) => <option key={k} value={k}>{t(`b.land_${k}`)}</option>)}
            </select>
          </F>
          <F label={t("b.lot")}><Input value={f.lot_size} onChange={set("lot_size")} data-testid="build-lot-input" /></F>
          <F label={t("b.size")}><Input value={f.home_size} onChange={set("home_size")} data-testid="build-size-input" /></F>
          <div className="grid grid-cols-2 gap-4">
            <F label={t("cl.beds")}><Input type="number" min={0} value={f.bedrooms} onChange={set("bedrooms")} data-testid="build-beds-input" /></F>
            <F label={t("cl.baths")}><Input type="number" min={0} value={f.bathrooms} onChange={set("bathrooms")} data-testid="build-baths-input" /></F>
          </div>
          <F label={t("b.budget")}>
            <select className={sel} value={f.budget} onChange={set("budget")} data-testid="build-budget-select"><option value="">{t("b.choose")}</option>{BUDGETS.map((b) => <option key={b}>{b}</option>)}</select>
          </F>
          <F label={t("b.timeline")}>
            <select className={sel} value={f.timeline} onChange={set("timeline")} data-testid="build-timeline-select"><option value="">{t("b.choose")}</option>{TIMELINES.map((k) => <option key={k} value={t(`b.tl_${k}`)}>{t(`b.tl_${k}`)}</option>)}</select>
          </F>
          <F label={t("b.style")} wide><Input value={f.style} onChange={set("style")} data-testid="build-style-input" /></F>
          <F label={t("b.desc")} wide><Textarea rows={4} value={f.description} onChange={set("description")} data-testid="build-desc-input" /></F>
          <F label={t("b.files")} wide><Input type="file" multiple accept="image/*,application/pdf" onChange={(e) => setFiles([...e.target.files].slice(0, 8))} data-testid="build-files-input" /></F>
          <LocateMe geo={f.geo} onFound={({ geo, city }) => setF((p) => ({ ...p, geo, city: p.city || city }))} />
          <Button type="submit" disabled={busy} className="sm:col-span-2 h-12 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold" data-testid="build-submit-btn">
            {busy && <Loader2 className="h-4 w-4 me-2 animate-spin" />}{t("b.submit")}
          </Button>
        </form>
      </section>
    </div>
  );
}
